import { Fragment, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ChevronRight, Database, Maximize2, Pencil } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { et } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { DashboardPanel } from "@/components/building/DashboardPanel";
import { SettingsSectionLabel } from "@/components/settings/SettingsSectionLabel";
import { useBuildingWidgetData } from "@/hooks/buildings/useBuildingWidgetData";
import { useStores } from "@/stores/useStores";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { deriveCompleteness } from "./completeness";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";

// Match the /data page's semantic status colours (its progress ring, count
// pills, and section-rail bars) so the two surfaces read the same states the
// same way: confirmed = success green, pending = warning yellow, empty = gray,
// conflicts = red (the "problem" colour).
const STAT = {
  confirmed: "#009e46", // success — hsl(var(--success))
  pending: "#ffd900", // warning — hsl(var(--warning))
  empty: "#cacbce", // neutral gray
  conflicts: "#ef4129", // error / primary red
};

interface BuildingDataSummaryProps {
  buildingId: string;
  orgId: string | undefined;
}

function pctOf(part: number, total: number): string {
  return `${total > 0 ? (part / total) * 100 : 0}%`;
}

/**
 * Building Data completeness summary for the dashboard — the right-hand
 * column of the "Message board | Building data" section: a label row on the
 * white page above a white dashboard card. One segmented progress bar plus
 * a dot legend replace the former donut + stat boxes, and conflicts / empty
 * fields surface as actionable light-tinted rows (red for conflicts) that
 * deep-link into the Building Data sidebar (focusField / openWithFilter on
 * the widget store). Reuses the shared useBuildingWidgetData query (a cache
 * hit — BuildingHeader already loads it).
 */
export function BuildingDataSummary({ buildingId, orgId }: BuildingDataSummaryProps) {
  const { t, language } = useLanguage();
  const s = t.buildingDataSummary;
  const tw = t.buildings.buildingDataWidget;
  const { buildingDataWidgetStore } = useStores();
  const navigate = useNavigate();
  // Deep-links land on the Building Data page (the sidebar is retired): the
  // one-shot focus/filter request goes on the widget store, then we navigate.
  const openDataPage = () => navigate(`/buildings/${buildingId}/data`);
  const { data, isLoading, isError, refetch } = useBuildingWidgetData(buildingId, orgId);
  const c = useMemo(() => deriveCompleteness(data), [data]);

  // The bar's purple segment and the legend's "Pending" count exclude
  // conflicted fields — those get the red segment / their own legend entry,
  // so the four segments always sum to the total.
  const pendingClean = Math.max(0, c.pending - c.conflicts);

  const relTime =
    data?.lastUpdated != null
      ? (() => {
          try {
            return formatDistanceToNow(new Date(data.lastUpdated as string), {
              addSuffix: true,
              locale: language === "et" ? et : undefined,
            });
          } catch {
            return null;
          }
        })()
      : null;

  const labelRow = (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <SettingsSectionLabel>{s.title}</SettingsSectionLabel>
      <div className="flex flex-wrap items-center gap-2">
        {relTime && (
          <span className="text-[11px] text-muted-foreground">
            {s.updated.replace("{time}", relTime)}
          </span>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="min-h-[32px] gap-1.5 px-2 text-[13px]"
          aria-label={s.openSidebar}
          onClick={openDataPage}
          {...pageInfoTarget("building.buildingData")}
        >
          <Maximize2 className="h-3.5 w-3.5" />
          {s.openSidebar}
        </Button>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex flex-col">
        {labelRow}
        <DashboardPanel className="mt-3 flex-1 space-y-4">
          <Skeleton className="h-9 w-48" />
          <Skeleton className="h-3 w-full rounded-full" />
          <Skeleton className="h-4 w-64" />
        </DashboardPanel>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col">
        {labelRow}
        <DashboardPanel className="mt-3 flex flex-1 flex-col items-center justify-center px-4 py-5 text-center">
          <AlertTriangle className="mb-2 h-6 w-6 text-red-500" />
          <p className="text-sm font-medium">{tw.dataLoadError}</p>
          <Button variant="outline" size="sm" className="mt-4" onClick={() => void refetch()}>
            {tw.retryData}
          </Button>
        </DashboardPanel>
      </div>
    );
  }

  if (c.total === 0) {
    return (
      <div className="flex flex-col">
        {labelRow}
        <DashboardPanel className="mt-3 flex flex-1 flex-col items-center justify-center px-4 py-5 text-center">
          <Database className="mb-2 h-6 w-6 text-muted-foreground/40" />
          <p className="text-sm font-medium">{s.emptyTitle}</p>
          <p className="mt-1 max-w-xs text-xs text-muted-foreground">{s.emptyHint}</p>
        </DashboardPanel>
      </div>
    );
  }

  const legendItems: Array<{ label: string; value: number; color: string }> = [
    { label: s.confirmed, value: c.confirmed, color: STAT.confirmed },
    { label: s.pending, value: pendingClean, color: STAT.pending },
    { label: s.empty, value: c.empty, color: STAT.empty },
    ...(c.conflicts > 0
      ? [
          {
            label: c.conflicts === 1 ? s.conflictSingular : s.conflicts,
            value: c.conflicts,
            color: STAT.conflicts,
          },
        ]
      : []),
  ];

  const conflictText =
    c.conflicts === 1
      ? s.conflictRowOne.replace("{field}", c.conflictFields[0]?.label ?? "")
      : s.conflictRowMany.replace("{count}", String(c.conflicts));

  return (
    <div className="flex flex-col">
      {labelRow}

      <DashboardPanel className="mt-3 flex flex-1 flex-col">
        {/* Actual confirmed fields, rather than an implied building-progress score. */}
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="text-sm font-medium tabular-nums">
            {s.fieldsFilled
              .replace("{confirmed}", String(c.confirmed))
              .replace("{total}", String(c.total))}
          </span>
        </div>

        {/* One segmented progress bar. The conflict segment keeps a minimum
          visible width (flex-shrink-0) so a single conflict never disappears;
          the proportional segments absorb the few pixels it borrows. */}
        <div
          role="img"
          aria-label={`${c.confirmed} ${s.confirmed}, ${pendingClean} ${s.pending}, ${c.empty} ${s.empty}, ${c.conflicts} ${s.conflicts}`}
          className="mt-3 flex h-1.5 w-full overflow-hidden rounded-full bg-muted"
        >
          <div style={{ width: pctOf(c.confirmed, c.total), backgroundColor: STAT.confirmed }} />
          <div style={{ width: pctOf(pendingClean, c.total), backgroundColor: STAT.pending }} />
          <div style={{ width: pctOf(c.empty, c.total), backgroundColor: STAT.empty }} />
          {c.conflicts > 0 && (
            <div
              className="flex-shrink-0"
              style={{
                width: pctOf(c.conflicts, c.total),
                minWidth: 8,
                backgroundColor: STAT.conflicts,
              }}
            />
          )}
        </div>

        {/* One-line dot legend. */}
        <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-muted-foreground">
          {legendItems.map((item, i) => (
            <Fragment key={item.label}>
              {i > 0 && <span className="text-muted-foreground/50">·</span>}
              <span className="flex items-center gap-1.5">
                <span
                  className="h-2 w-2 flex-shrink-0 rounded-full"
                  style={{ backgroundColor: item.color }}
                />
                <span className="font-medium tabular-nums text-foreground">{item.value}</span>
                {item.label}
              </span>
            </Fragment>
          ))}
        </div>

        {/* Actionable rows — light tint inside the white card, deep-linking
          into the Building Data page. */}
        <div className="mt-3 flex flex-wrap gap-2">
          {c.conflicts > 0 && (
            <button
              type="button"
              onClick={() => {
                const first = c.conflictFields[0];
                if (first) buildingDataWidgetStore.focusField(first.id);
                openDataPage();
              }}
              className="flex min-w-0 flex-1 basis-64 items-center gap-2.5 rounded-lg bg-red-50 px-3 py-2.5 text-left text-[13px] font-medium text-red-900 transition-colors hover:bg-red-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400/50"
            >
              <AlertTriangle className="h-4 w-4 flex-shrink-0 text-red-500" />
              <span className="min-w-0 flex-1 truncate">{conflictText}</span>
              <ChevronRight className="h-4 w-4 flex-shrink-0 text-red-400" />
            </button>
          )}
          {c.empty > 0 && (
            <button
              type="button"
              onClick={() => {
                buildingDataWidgetStore.openWithFilter("empty");
                openDataPage();
              }}
              className="flex min-w-0 flex-1 basis-64 items-center gap-2.5 rounded-lg bg-[#f7f7f8] px-3 py-2.5 text-left text-[13px] font-medium text-neutral-700 transition-colors hover:bg-[#efeff1] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              <Pencil className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate">
                {s.emptyRow.replace("{count}", String(c.empty))}
              </span>
              <ChevronRight className="h-4 w-4 flex-shrink-0 text-neutral-400" />
            </button>
          )}
        </div>
      </DashboardPanel>
    </div>
  );
}
